import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { backState } from '../lib/backTo';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { useFetch } from '../lib/useFetch';
import { getPaymentTransactions, getDunning } from '../lib/api';
import { fmtCents } from '../lib/format';

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—');
const PAY_TONE: Record<string, 'success' | 'error' | 'pending' | 'neutral'> = { succeeded: 'success', failed: 'error', refunded: 'neutral', pending: 'pending' };

const BACK_TO_PAYMENTS = backState({ to: '/payments', label: 'Back to Payments' });

export const PaymentsPage = ({ tabs }: { tabs?: ReactNode } = {}) => {
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const txns = useFetch(() => getPaymentTransactions({ status: status || undefined, provider: provider || undefined }), [status, provider]);
  const dunning = useFetch(getDunning);

  return (
    <Page>
      <PageHeader title="Payments" sub="Charge attempts, outcomes, and invoices needing attention" tabs={tabs} />

      <Async state={dunning} isEmpty={(d) => d.invoices.length === 0} emptyLabel="">
        {({ invoices }) =>
          invoices.length === 0 ? (
            <></>
          ) : (
            <div>
              <div className="section-title" style={{ color: 'var(--status-error-ink,#b91c1c)' }}>
                Needs attention — {invoices.length} due invoice(s) not charged
              </div>
              <PCard>
                <table className="p-table">
                  <thead><tr><th>Invoice</th><th>Client</th><th style={{ textAlign: 'right' }}>Due</th><th>Charge date</th><th>Last failure</th></tr></thead>
                  <tbody>
                    {invoices.map((r) => (
                      <tr key={r.id}>
                        <td className="mono"><Link to={`/invoices/${r.id}`} state={BACK_TO_PAYMENTS}>{r.invoiceNumber ?? r.id.slice(-6)}</Link></td>
                        <td><Link to={`/clients/${r.clientId}`}>{r.clientName}</Link></td>
                        <td className="tabular" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtCents(r.amountDueCents)}</td>
                        <td className="muted">{fmtDate(r.chargeAt)}</td>
                        <td className="muted" style={{ fontSize: 12, color: 'var(--status-error-ink,#b91c1c)' }}>{r.lastFailure ?? 'no card / not charged'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </PCard>
            </div>
          )
        }
      </Async>

      <div>
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          Transactions
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ marginLeft: 'auto' }}>
            <option value="">All statuses</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
            <option value="pending">Pending</option>
          </select>
          <select className="select" value={provider} onChange={(e) => setProvider(e.target.value)}>
            <option value="">All providers</option>
            <option value="stripe">Stripe</option>
            <option value="hyperswitch">Hyperswitch</option>
            <option value="manual">Manual</option>
          </select>
        </div>
        <Async state={txns} isEmpty={(d) => d.payments.length === 0} emptyLabel="No payments recorded yet.">
          {({ payments }) => (
            <PCard>
              <table className="p-table">
                <thead><tr><th>When</th><th>Client</th><th>Invoice</th><th>Provider</th><th>Status</th><th style={{ textAlign: 'right' }}>Amount</th><th>Detail</th></tr></thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td className="muted">{fmtDate(p.attemptedAt)}</td>
                      <td><Link to={`/clients/${p.clientId}`}>{p.clientName}</Link></td>
                      <td className="mono"><Link to={`/invoices/${p.invoiceId}`} state={BACK_TO_PAYMENTS}>{p.invoiceNumber ?? p.invoiceId.slice(-6)}</Link></td>
                      <td className="muted">{p.provider}</td>
                      <td><StatusPill tone={PAY_TONE[p.status] ?? 'neutral'}>{p.status}</StatusPill></td>
                      <td className="tabular" style={{ textAlign: 'right' }}>{fmtCents(p.amountCents)}</td>
                      <td className="muted" style={{ fontSize: 12 }}>{p.failureReason ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </PCard>
          )}
        </Async>
      </div>
    </Page>
  );
};
